import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { MdDelete } from "react-icons/md";
import { useAuthStore } from "../../context/useAuthStore";
import axios from "axios";
import { PhoneNumberUtil, PhoneNumberFormat } from "google-libphonenumber";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const AdminId = import.meta.env.VITE_ADMIN_ID;
const ProductTeam = import.meta.env.VITE_PRODUCT_TEAM_ID;

const tableHeaders = [
  "Sl.No",
  "Name",
  "Email ID",
  "Phone Number",
  "Role",
  "Action",
];

type Admin = {
  fullName: string;
  emailId: string;
  phoneNumber: string;
  roleId: string;
  userId: string;
};

const phoneUtil = PhoneNumberUtil.getInstance();

const MyAdmins: React.FC = () => {
  const { user, token } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [admins, setAdmins] = useState<Admin[]>([]);

  const formatPhoneNumber = (number: string, region = "IN") => {
    try {
      const phoneNumber = phoneUtil.parseAndKeepRawInput(number, region);
      const isValidNumber = phoneUtil.isValidNumber(phoneNumber);
      let formatted = phoneUtil.format(phoneNumber, PhoneNumberFormat.NATIONAL);
      if (formatted.startsWith("0")) formatted = formatted.substring(1);
      return { formatted, isValidNumber };
    } catch {
      return { formatted: number, isValidNumber: false };
    }
  };

  useEffect(() => {
    const fetchAdmins = async () => {
      if (!user?.userId) {
        toast.error("User not found.");
        return;
      }

      setLoading(true);
      try {
        const res = await axios.get(`${API_BASE_URL}/user?userId=`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        });

        if (res.data?.success && Array.isArray(res.data.success)) {
          const data: Admin[] = res.data.success;
          const filteredAdmins = data.filter(
            (u) => u.roleId === AdminId || u.roleId === ProductTeam
          );
          setAdmins(filteredAdmins);
        } else {
          setAdmins([]);
          toast.error("No Admins found.");
        }
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        toast.error("Failed to fetch admins.");
        setAdmins([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAdmins();
  }, [user?.userId, token]);

  const handleDelete = async (userIdToDelete: string) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this admin?"
    );
    if (!confirmDelete) return;

    try {
      const response = await axios.delete(`${API_BASE_URL}/user`, {
        params: { userId: userIdToDelete },
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      });

      if (response.data?.success) {
        setAdmins((prev) =>
          prev.filter((admin) => admin.userId !== userIdToDelete)
        );
        toast.success("Admin deleted successfully.");
      } else {
        toast.error("Failed to delete admin.");
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      toast.error("An error occurred while deleting the admin.");
    }
  };

  const getRoleBadge = (roleId: string) => {
    if (roleId === AdminId) {
      return (
        <span className="px-2 py-1 rounded-md text-xs font-semibold bg-gray-100 text-gray-700">
          Admin
        </span>
      );
    }

    if (roleId === ProductTeam) {
      return (
        <span className="px-2 py-1 rounded-md text-xs font-semibold bg-purple-100 text-purple-700">
          Product Team
        </span>
      );
    }

    return (
      <span className="px-2 py-1 rounded-md text-xs font-semibold bg-gray-200 text-gray-600">
        Unknown Role
      </span>
    );
  };

  return (
    <div className="subjectSection">
      <h2 className="subjectTitle">List of Active Admins</h2>
      <div className="borderLine"></div>
      {loading ? (
        <p className="subjectNotFound">Loading...</p>
      ) : admins.length === 0 ? (
        <p className="subjectNotFound">No Admins found.</p>
      ) : (
        <section className="subjectTableSection">
          <div className="overflow-x-auto">
            <div className="max-h-[calc(100vh-17.75rem)] overflow-y-auto">
              <table className="min-w-full border-collapse">
                <thead className="sticky top-0 bg-white z-10">
                  <tr className="bg-gray-200">
                    {tableHeaders.map((header, index) => (
                      <th key={index} className="tableHeadValue">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {admins.map((admin, index) => {
                    const { formatted, isValidNumber } = formatPhoneNumber(
                      admin.phoneNumber
                    );
                    return (
                      <tr key={index} className="tableBodyRow">
                        <td className="tableBodyValue">{index + 1}</td>
                        <td className="tableBodyValue">{admin.fullName}</td>
                        <td className="tableBodyValue">{admin.emailId}</td>
                        <td
                          className={`tableBodyValue ${
                            !isValidNumber ? "underline text-red-600" : ""
                          }`}
                        >
                          {formatted}
                        </td>
                        <td className="tableBodyValue">
                          {getRoleBadge(admin.roleId)}
                        </td>
                        <td className="tableBodyValue">
                          <button
                            onClick={() => handleDelete(admin.userId)}
                            className="bg-red-100 text-red-500 p-2 cursor-pointer rounded-full hover:bg-red-500 hover:text-white transition"
                          >
                            <MdDelete className="md:w-5 md:h-5 w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default MyAdmins;
